import { useState, useEffect } from "react";
import * as Location from "expo-location";
import { Platform } from "react-native";

export type UserLocation = {
  latitude: number;
  longitude: number;
} | null;

/**
 * Hook to get the user's current location
 * Works on both native (expo-location) and web (browser Geolocation API)
 * @returns User's location or null if unavailable
 */
export function useUserLocation(): {
  location: UserLocation;
  isLoading: boolean;
  error: Error | null;
} {
  const [location, setLocation] = useState<UserLocation>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function getLocation() {
      try {
        if (Platform.OS === "web") {
          // Use browser's Geolocation API for web
          if (!navigator.geolocation) {
            if (isMounted) {
              setError(
                new Error("Geolocation is not supported by this browser"),
              );
              setIsLoading(false);
            }
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (isMounted) {
                setLocation({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                });
                setIsLoading(false);
              }
            },
            (err) => {
              if (isMounted) {
                let errorMessage = "Failed to get location";
                if (err.code === 1) {
                  errorMessage = "Location permission denied";
                } else if (err.code === 2) {
                  errorMessage = "Location unavailable";
                } else if (err.code === 3) {
                  errorMessage = "Location request timeout";
                }
                setError(new Error(errorMessage));
                setIsLoading(false);
              }
            },
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000, // Cache for 5 minutes
            },
          );
        } else {
          // Use expo-location for native platforms
          // Request permissions
          const { status } = await Location.requestForegroundPermissionsAsync();
          if (status !== "granted") {
            if (isMounted) {
              setError(new Error("Location permission denied"));
              setIsLoading(false);
            }
            return;
          }

          // Get current position
          const currentLocation = await Location.getCurrentPositionAsync({
            accuracy: Location.Accuracy.Balanced,
          });

          if (isMounted) {
            setLocation({
              latitude: currentLocation.coords.latitude,
              longitude: currentLocation.coords.longitude,
            });
            setIsLoading(false);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to get location"),
          );
          setIsLoading(false);
        }
      }
    }

    getLocation();

    return () => {
      isMounted = false;
    };
  }, []);

  return { location, isLoading, error };
}
